package co.maytes.standupbot.dashboard.components.widgets

import androidx.compose.runtime.Composable
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.B
import org.jetbrains.compose.web.dom.Code
import org.jetbrains.compose.web.dom.Em
import org.jetbrains.compose.web.dom.Text

sealed interface SlackSegment {
    data class Plain(val text: String) : SlackSegment

    data class Bold(val text: String) : SlackSegment

    data class Italic(val text: String) : SlackSegment

    data class Mono(val text: String) : SlackSegment

    data class Link(val label: String, val href: String) : SlackSegment
}

object SlackMrkdwn {
    private val token =
        Regex("<(https?://[^|>\\s]+)(?:\\|([^>]*))?>|<@[A-Z0-9]+(?:\\|([^>]*))?>|\\*([^*\\n]+)\\*|_([^_\\n]+)_|`([^`\\n]+)`")

    fun parse(source: String): List<SlackSegment> {
        val out = mutableListOf<SlackSegment>()
        var cursor = 0
        token.findAll(source).forEach { match ->
            if (match.range.first > cursor) out += SlackSegment.Plain(source.substring(cursor, match.range.first))
            val (url, label, mention, bold, italic, mono) = match.destructured
            val wordBefore = match.range.first > 0 && source[match.range.first - 1].isLetterOrDigit()
            val wordAfter = match.range.last + 1 < source.length && source[match.range.last + 1].isLetterOrDigit()
            val emphasis = bold.isNotEmpty() || italic.isNotEmpty()
            out +=
                when {
                    emphasis && (wordBefore || wordAfter) -> SlackSegment.Plain(match.value)
                    url.isNotEmpty() -> SlackSegment.Link(label.ifBlank { shorten(url) }, url)
                    bold.isNotEmpty() -> SlackSegment.Bold(bold)
                    italic.isNotEmpty() -> SlackSegment.Italic(italic)
                    mono.isNotEmpty() -> SlackSegment.Mono(mono)
                    else -> SlackSegment.Plain(mention.takeIf { it.isNotBlank() }?.let { "@$it" } ?: match.value)
                }
            cursor = match.range.last + 1
        }
        if (cursor < source.length) out += SlackSegment.Plain(source.substring(cursor))
        return out
    }

    private fun shorten(url: String): String = url.removePrefix("https://").removePrefix("http://").removePrefix("www.")
}

@Composable
fun SlackMrkdwnText(source: String) {
    SlackMrkdwn.parse(source).forEach { segment ->
        when (segment) {
            is SlackSegment.Plain -> Text(segment.text)
            is SlackSegment.Bold -> B { Text(segment.text) }
            is SlackSegment.Italic -> Em { Text(segment.text) }
            is SlackSegment.Mono -> Code { Text(segment.text) }
            is SlackSegment.Link ->
                A(
                    href = segment.href,
                    attrs = {
                        attr("target", "_blank")
                        attr("rel", "noopener noreferrer")
                    },
                ) { Text(segment.label) }
        }
    }
}
